import clsx from 'clsx';
import React from 'react';
import { Button } from 'react-aria-components';

import { FormFieldLabel } from './FormFieldLabel';
import { FormFieldSupportingText } from './FormFieldSupportingText';
import { BaseFormFieldProps } from './types';

type TextAreaInputFieldProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  BaseFormFieldProps & {
    value?: string;
  };

export const TextAreaInputField = ({
  description,
  descriptionLeadingIconName,
  descriptionPosition = 'below',
  errorMessage,
  errorLeadingIconName,
  className,
  label,
  onChange,
  maxLength = 255,
  ...props
}: TextAreaInputFieldProps) => {
  const id = React.useId();
  const inputId = `field-${id}`;
  const labelId = `label-${id}`;
  const descriptionId = `desc-${id}`;
  const errorId = `error-${id}`;

  const [characterCount, setCharacterCount] = React.useState(props.value?.length ?? 0);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = React.useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const newHeight = Math.max(textarea.scrollHeight, 80);
      textarea.style.height = `${newHeight}px`;
    }
  }, [textareaRef]);

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = event.target.value;

    // Truncate when value exceeds maxLength
    if (newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    setCharacterCount(newValue.length);

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: newValue,
      },
    };

    onChange?.(newEvent);
    adjustTextareaHeight();
  };

  const handleClear = () => {
    setCharacterCount(0);
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLTextAreaElement>);
    adjustTextareaHeight();
  };

  React.useEffect(() => {
    if (props.value !== undefined) {
      const newLength = props.value.length;
      setCharacterCount(newLength);
    }
    adjustTextareaHeight();
  }, [props.value, adjustTextareaHeight]);

  return (
    <div className={clsx('relative w-full', className)}>
      <FormFieldLabel id={labelId} htmlFor={inputId}>
        {label}
      </FormFieldLabel>

      {description && descriptionPosition === 'above' && (
        <FormFieldSupportingText id={descriptionId} leadingIconName={descriptionLeadingIconName} position="above">
          {description}
        </FormFieldSupportingText>
      )}

      <div
        className={clsx(
          'border-1 rounded-xs overflow-hidden relative',
          errorMessage ? 'border-error' : 'border-outline-l4',
          'bg-surface-l0 focus-within:ring-primary focus-within:ring-2 focus-within:ring-blue-400 focus-within:ring-offset-2 focus-within:outline-none',
        )}
      >
        <textarea
          {...props}
          className="w-full px-16 pt-16 pb-12 resize-none min-h-[80px] overflow-hidden outline-none"
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={`${descriptionId} ${errorId}`}
          onChange={handleChange}
          maxLength={maxLength}
          ref={textareaRef}
        />

        {/* Divider style */}
        <div className="border-t border-outline-l2 mt-4 mb-4 mx-16" />

        {/* Counter + Clear button */}
        <div className="flex items-center justify-between text-sm text-on-surface-variant px-16 pb-4 min-h-[40px]">
          <span className="text-14">
            {characterCount} / {maxLength}
          </span>
          {characterCount > 0 && (
            <Button
              onPress={handleClear}
              className="text-primary hover:text-primary-dark cursor-pointer font-medium"
              aria-label="Clear text"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {description && descriptionPosition === 'below' && (
        <FormFieldSupportingText id={descriptionId} leadingIconName={descriptionLeadingIconName} position="below">
          {description}
        </FormFieldSupportingText>
      )}

      {errorMessage && (
        <FormFieldSupportingText id={errorId} leadingIconName={errorLeadingIconName} isError>
          {errorMessage}
        </FormFieldSupportingText>
      )}
    </div>
  );
};
